/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import {
  fetchKCs,
  createKnowledgeComponent,
  deleteKnowledgeComponent as _deleteKnowledgeComponent,
} from "../../../api";
import { RootState } from "../../store";
import { Edits, KnowledgeComponent, LoadStatus } from "../../../types";

export interface KnowledgeComponentsState {
  knowledgeComponents: Record<string, KnowledgeComponent>;
  knowledgeComponentEdits: Record<string, Edits<KnowledgeComponent>>;
  knowledgeComponentLoadStatus: LoadStatus;
}

const initialState: KnowledgeComponentsState = {
  knowledgeComponents: {},
  knowledgeComponentEdits: {},
  knowledgeComponentLoadStatus: LoadStatus.NONE,
};

/** Actions */

export const loadKnowledgeComponents = createAsyncThunk(
  "knowledgeComponents/loadKnowledgeComponents",
  async (_, thunkAPI): Promise<KnowledgeComponent[]> => {
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    return await fetchKCs();
  }
);

export const addOrUpdateKnowledgeComponent = createAsyncThunk(
  "knowledgeComponents/addOrUpdateKnowledgeComponent",
  async (
    data: Partial<KnowledgeComponent>,
    thunkAPI
  ): Promise<KnowledgeComponent> => {
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    return await createKnowledgeComponent(state.login.accessToken, data);
  }
);

export const deleteKnowledgeComponent = createAsyncThunk(
  "knowledgeComponents/deleteKnowledgeComponent",
  async (id: string, thunkAPI): Promise<void> => {
    if (!id) throw new Error("id required");
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    await _deleteKnowledgeComponent(state.login.accessToken, id);
  }
);

export const knowledgeComponentsSlice = createSlice({
  name: "knowledgeComponents",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // load knowledgeComponents
      .addCase(loadKnowledgeComponents.pending, (state) => {
        state.knowledgeComponentLoadStatus = LoadStatus.IN_PROGRESS;
      })
      .addCase(loadKnowledgeComponents.fulfilled, (state, action) => {
        for (const knowledgeComponent of action.payload) {
          state.knowledgeComponents[knowledgeComponent.id] = knowledgeComponent;
          state.knowledgeComponentEdits[knowledgeComponent.id] = {
            oldData: knowledgeComponent,
          };
        }
        state.knowledgeComponentLoadStatus = LoadStatus.DONE;
      })
      .addCase(loadKnowledgeComponents.rejected, (state) => {
        state.knowledgeComponentLoadStatus = LoadStatus.FAILED;
      })
      // add or update knowledgeComponent
      .addCase(addOrUpdateKnowledgeComponent.fulfilled, (state, action) => {
        const knowledgeComponent = action.payload;
        if (state.knowledgeComponents[knowledgeComponent.id]) {
          state.knowledgeComponentEdits[knowledgeComponent.id].newData =
            knowledgeComponent;
        } else {
          state.knowledgeComponentEdits[knowledgeComponent.id] = {
            oldData: knowledgeComponent,
            created: true,
          };
        }
        state.knowledgeComponents[knowledgeComponent.id] = knowledgeComponent;
      })
      // delete knowledgeComponent
      .addCase(deleteKnowledgeComponent.fulfilled, (state, action) => {
        const id = action.meta.arg;
        delete state.knowledgeComponents[id];
      });
  },
});

export default knowledgeComponentsSlice.reducer;
